import { Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { GiftCard, Denomination } from '~models';
import { environment } from '../../../environments/environment';
import { isRenderingForMobileApp } from 'src/constants';
import { Utils } from 'src/app/utils/utilities';
import {
  CheckoutService,
  ChargeStatus,
  OrderPollWebResult,
  ZipYiService,
  OptimizelyService,
  ThanksScriptService,
} from '~services';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ZipYiContext } from '~services/biocatch/zip-yi-context.enum';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-complete-view',
  templateUrl: './complete.component.html',
  styleUrls: ['./complete.component.scss'],
})
export class CompleteViewComponent implements OnInit, OnDestroy {
  public selectedDenomination: Denomination = new Denomination();
  public giftCard?: GiftCard = null;
  public console = console;
  public loaded = false;
  public appPrefix = environment.appPrefix;
  public paramOrderId: any;
  public isLoading: boolean;
  public orderWebResult?: OrderPollWebResult;
  public confirmedRemainPending = false;
  get id() {
    return this.activatedRoute.snapshot.params.id;
  }
  public dataGifting: any;
  public existingCheckout: any;
  public deliveryDateUI: any;
  public checkoutInfo: any;

  private destroy$ = new Subject<boolean>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private ngRouter: Router,
    private checkoutService: CheckoutService,
    public readonly location: Location,
    private zipYiService: ZipYiService,
    private optimizelyService: OptimizelyService,
    private thanksService: ThanksScriptService,
  ) {
    this.paramOrderId = '';
    this.isLoading = true;
    this.dataGifting = {};
    this.deliveryDateUI = '';
  }

  async ngOnInit() {
    this.zipYiService.setContext(ZipYiContext.PurchaseCompletePage);
    await this.checkData();
    this.thanksService.addThanksScript({
      email: this.existingCheckout.shopper.email,
      giftCardName: this.checkoutInfo.giftcard.brand,
      giftCardValue: this.existingCheckout.order.amount,
      categories: this.checkoutInfo.giftcard.categories ?? [],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  goToHome() {
    if (!this.isLoading) {
      this.ngRouter.navigate(['au/giftcards']);
    }
  }

  async checkData() {
    this.isLoading = true;
    this.deliveryDateUI = '';
    const d = _.get(this.activatedRoute, 'snapshot.queryParams.d');
    const existing_order_id = _.get(this.activatedRoute, 'snapshot.queryParams.orderId');
    const simPendingSecs = _.get(this.activatedRoute, 'snapshot.queryParams.simPendingSecs');
    let simulatePendingTil;
    if (simPendingSecs) {
      simulatePendingTil = new Date().getTime() + 1000 * simPendingSecs;
    }

    const checkoutInfo = JSON.parse(decodeURIComponent(escape(atob(d))));
    console.log('Received checkoutInfo', checkoutInfo);
    if (checkoutInfo.result === 'approved') {
      this.checkoutInfo = checkoutInfo;
      this.giftCard = checkoutInfo.giftcard;
      try {
        this.existingCheckout = await this.checkoutService.getCheckout(
          checkoutInfo.checkoutId,
          checkoutInfo.giftcard.merchantUniqueId,
          checkoutInfo.giftcard.merchantId,
        );
        console.log('Retrieved existing checkout: ', this.existingCheckout);

        this.deliveryDateUI = '';

        if (this.existingCheckout.gifting) {
          this.dataGifting = { ...this.existingCheckout.gifting };
          console.log('this.dataGifting:', this.dataGifting);
        }

        if (Utils.checkValid(this.existingCheckout.metadata.gifting_deliveryDate)) {
          this.deliveryDateUI = moment(this.existingCheckout.metadata.gifting_deliveryDate).format(
            'DD/MM/YYYY',
          );
        }

        /// Check if this one is already completed
        const completed = this.existingCheckout['state'] === 'completed';

        const chargeInfo = {
          id: checkoutInfo.checkoutId,
          price: _.get(this.existingCheckout, 'order.amount'),
          productId: checkoutInfo.productId,
          name: checkoutInfo.name,
          email: checkoutInfo.email,
          vendor: checkoutInfo.giftcard.vendor,
          discount: checkoutInfo.giftcard.discount,
          cardName: checkoutInfo.giftcard.brand,
          merchantUniqueId: checkoutInfo.giftcard.merchantUniqueId,
          merchantId: checkoutInfo.giftcard.merchantId,
        };

        let checkoutResult = {
          zipStatus: ChargeStatus.PENDING,
          orderId: existing_order_id,
        };

        if (!existing_order_id && !completed) {
          checkoutResult = await this.checkoutService.charge(chargeInfo);
          // put the orderId on the URL, if the user reloads this page it'd know checkout is already done.
          const newState = {
            ..._.get(this.activatedRoute, 'snapshot.queryParams'),
            orderId: checkoutResult.orderId,
          };
          this.ngRouter.navigate(['/finalise'], { replaceUrl: true, queryParams: newState });
        }

        let trapToUrl = window.location.href;

        if (trapToUrl.indexOf('orderId') < 0 && checkoutResult.orderId) {
          trapToUrl = trapToUrl + '&orderId=' + checkoutResult.orderId;
          console.log('Trap to URL: ', trapToUrl);
        }

        // Prevent user from going back
        // Notice that the effort of this is limited.
        // Chrome is smart enough not to register the event listener
        window.addEventListener('popstate', function () {
          console.log('Attemp to navigate to URL: ', trapToUrl);
          window.location.href = trapToUrl;
        });

        // Always poll for order result. This will wait for success for a certain period of time.
        this.orderWebResult = await this.checkoutService.orderPollWeb({
          orderId: checkoutResult.orderId,
          simulatePendingTil,
          disableOnTheSpotDelivery: false,
          pollForSecs: 10,
        });

        this.isLoading = false;
        this.deliveryDateUI = '';

        if (Utils.checkValid(this.orderWebResult.gifting)) {
          if (
            Utils.checkValid(this.orderWebResult.gifting.gifting?.deliveryDate) &&
            this.orderWebResult.gifting.gifting?.deliveryDate !== ''
          ) {
            this.deliveryDateUI = moment(this.orderWebResult.gifting.gifting?.deliveryDate).format(
              'DD/MM/YYYY',
            );
          }
        }

        if (
          [
            ChargeStatus.PENDING,
            ChargeStatus.PENDING_NO_RETRY,
            ChargeStatus.PENDING_FULFILLMENT,
          ].includes(this.orderWebResult.zipStatus)
        ) {
          // this.ngRouter.navigate(['/pending']);
          this.confirmedRemainPending = true;
        } else {
          const error = new Error(
            `Received invalid orderWebResult zipStatus: '${this.orderWebResult.zipStatus}'`,
          );
          console.error(error.message);
          window.zbm?.reportError(error);
        }
      } catch (error) {
        window.zbm?.reportError(error);
        const responseError = error.error;
        if (
          responseError &&
          responseError.error &&
          responseError.error.indexOf('We are temporarily out of stock.') >= 0
        ) {
          this.ngRouter.navigate(['/error', 'We are temporarily out of stock.']);
        } else {
          this.ngRouter.navigate(['/error', "Uh oh, something's not right."]);
        }
      }
    } else {
      this.goToHome();
    }
  }
}
